// geometryUtils.js — Shared plain-geometry helpers used by multiple bespoke draw
// interactions and useMapEditor.js's generic single-click-and-spread fallback.
//
// Previously duplicated verbatim in two places (useMapEditor.js and
// customInteractions/fixedPointCollector.js): `generateSpread`.
//
// Previously duplicated with two DIFFERENT semantics across three files — kept here as two
// distinctly-named exports rather than collapsed into one, since callers rely on the
// difference:
//   - `distanceToSegmentClamped` — haversine great-circle distance (metres) from a WGS-84
//     [lng,lat] point to its CLAMPED projection onto a finite segment (t constrained to
//     [0,1]). Was `perpDistToSegment` in miscBespokeTools.js.
//   - `distanceToInfiniteLine` — planar Euclidean distance in the caller's own coordinate
//     space (EPSG:3857 map units in both original call sites) to the point's UNCLAMPED
//     projection onto the infinite line through A/B (t unconstrained). Was `distToLine` in
//     both lineSpineWidthTools.js and lineArcSideTools.js (those two were byte-for-byte
//     the same semantics, just written with slightly different argument styles/Math calls —
//     genuinely collapsed into one function here, unlike the clamped variant above).

import { distance as turfDistance } from '@turf/distance';

// generateSpread and distanceToInfiniteLine both operate on already-projected
// EPSG:3857 (Web Mercator) coordinates — i.e. plain 2D Cartesian map units, NOT
// WGS-84 [lng,lat] degrees. Turf's primitives (destination, bearing, distance,
// point-to-line-distance, etc.) universally assume geographic WGS-84 input and
// compute geodesically on a sphere; feeding them 3857 units directly would be
// nonsensical (they'd be interpreted as huge/garbage lng/lat values), and
// converting through toLonLat/fromLonLat first would swap this function's
// planar/projected semantics for geodesic ones. That's not a neutral swap here:
// generateSpread's radius is explicitly viewport-pixel-relative (resolution*80),
// meant to draw an evenly-spaced ring in *screen/map* space, not a geodesically
// true circle on the Earth's surface. Verified empirically — piping this through
// toLonLat → @turf/destination → fromLonLat reproduces the planar result at the
// equator but drifts by ~140m at mid-latitudes and >2km near 80°N for a 500m
// radius, because Web Mercator's scale factor grows with latitude. That would
// silently change on-screen spread geometry depending on where in the world the
// user is drawing — a real behavior change, not just float noise — so this stays
// hand-rolled planar trig rather than forcing a turf primitive that solves a
// different (geodesic) problem.

// Generate N points evenly spaced on a circle centered at center3857.
// Radius is viewport-relative: ~80px at current zoom.
// First point is placed north of center (angle = -π/2).
export function generateSpread(center3857, count, map) {
    const radius = map.getView().getResolution() * 80;
    return Array.from({ length: count }, (_, i) => {
        const angle = (2 * Math.PI * i / count) - Math.PI / 2;
        return [
            center3857[0] + radius * Math.cos(angle),
            center3857[1] + radius * Math.sin(angle),
        ];
    });
}

/**
 * Haversine distance (metres) from `pt` to its projection onto segment [segA, segB],
 * clamped so the projection can't fall outside the segment's endpoints.
 * All inputs are [lng, lat] in WGS-84.
 *
 * The clamped-projection point itself is found via a fast planar (flat-earth)
 * vector projection directly in [lng,lat] degree-space, NOT a true geodesic
 * nearest-point-on-great-circle — that's an intentional, pre-existing
 * approximation (this is what callers already rely on). Turf's own
 * `@turf/point-to-line-distance` (and the `nearestPointOnLine` it's built on)
 * instead computes the geodesically-correct clamped projection via great-circle
 * vector math, which is a DIFFERENT and more "correct" answer — verified
 * empirically to diverge from this function's existing output by ~440m on a
 * 100km segment and by tens of km on a 1000km segment at high latitude, while
 * matching almost exactly for short/local segments. Swapping the whole function
 * to that turf primitive would therefore silently change results for long
 * segments, which fails the "same inputs/outputs" requirement, so the manual
 * projection/clamping math is kept as-is. What DOES swap cleanly: the final
 * distance-from-projected-point calculation is plain haversine great-circle
 * distance, and `@turf/distance` uses the exact same formula and the same mean
 * Earth radius (6371008.8 m) as `ol/sphere`'s `getDistance` — verified to produce
 * identical output across short, long, high-latitude, and degenerate-segment
 * test cases — so that piece is now a genuine turf primitive instead of an
 * ol/sphere call.
 */
export function distanceToSegmentClamped(pt, segA, segB) {
    const ax = segA[0], ay = segA[1];
    const bx = segB[0], by = segB[1];
    const px = pt[0], py = pt[1];
    const abx = bx - ax, aby = by - ay;
    const ab2 = abx * abx + aby * aby;
    if (ab2 === 0) return turfDistance(pt, segA, { units: 'meters' }); // degenerate segment
    const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / ab2));
    const proj = [ax + t * abx, ay + t * aby];
    return turfDistance(pt, proj, { units: 'meters' });
}

/**
 * Planar Euclidean distance from point (px,py) to its projection onto the INFINITE line
 * through (ax,ay)-(bx,by) — the projection is not clamped to the segment. Operates in
 * whatever coordinate space the caller passes (both original call sites used EPSG:3857).
 *
 * Left as hand-rolled planar trig rather than swapped to a turf primitive: turf has no
 * "unclamped infinite line" concept at all (every turf distance/nearest-point helper — incl.
 * `@turf/point-to-line-distance` — clamps to the segment's endpoints by design), and turf's
 * geometry functions universally operate on geographic WGS-84 [lng,lat] input with geodesic
 * math, not on arbitrary planar coordinate spaces like the EPSG:3857 map units this function
 * actually receives from both call sites. There's no turf primitive that cleanly maps onto
 * "unclamped projection in planar/projected coordinates" without either changing the clamping
 * semantics or requiring a lng/lat round-trip that would turn this into geodesic distance
 * instead of planar map-unit distance — either of which would be a real behavior change, not
 * a pure internal-implementation swap. Kept as-is per the task's guidance to leave a variant
 * hand-rolled when turf doesn't cleanly fit rather than forcing a bad match.
 */
export function distanceToInfiniteLine(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return Math.hypot(px - ax, py - ay);
    const t = ((px - ax) * dx + (py - ay) * dy) / len2;
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
